declare const L: any;

import type { Marker, Geofence, GeofenceShape, GeofenceEvent } from '../../shared/wire';
import {
  MARKER_LABEL_LEN,
  MAX_MARKERS_PER_GROUP,
  MAX_GEOFENCES_PER_GROUP,
  MIN_POLYGON_VERTICES,
  MAX_POLYGON_VERTICES,
  MAX_GEOFENCE_RADIUS_M,
} from '../../shared/wire';
import { team } from '../state/team';
import { getAuthUserId } from '../state/auth-state';
import { MOCK } from '../state/device';
import { getLeafletMap } from '../state/app-state';
import { loadMineMarkers, addMineMarker, removeMineMarker } from '../storage/persistence';
import {
  apiGetGroupConsole,
  apiCreateMarker,
  apiUpdateMarker,
  apiDeleteMarker,
  apiCreateGeofence,
  apiUpdateGeofence,
  apiDeleteGeofence,
} from '../api/team';
import { $ } from '../ui/dom';
import { escapeHtml } from '../ui/dom';
import { showToast } from '../ui/toast';

// ── groupPathFor ─────────────────────────────────────────────────────────────
export function groupPathFor(group: string[] | null){
  if(!group || group.length !== 2 || !group.every(Boolean)) return null;
  // Full animal keys are the canonical wire form; the URL is just
  // group.join('-') (e.g. 'frog-frog'). Short codes stay confined to
  // tokenFor() output.
  return group.join('-');
}

// ── Console poll state ───────────────────────────────────────────────────────
// Additive team-overlay console poll (separate from the cohort poll above).
let consolePollTimer: any = null;
let consolePollLastNow: number | null = null;
// The poll cursors on the server's `now`, but pings carry a
// client-supplied `t` that can lag `now` (fix-time latency, cross-device
// interleave, clock skew). Re-fetching a bounded overlap window each poll
// and leaning on dedupePings catches the common near-boundary misses the
// bare `since=last_now` cursor would drop.
const GROUP_POLL_OVERLAP_MS = 60_000;

// ── Team layer state ─────────────────────────────────────────────────────────
let teamLayer: any = null;             // L.layerGroup on leafletMap
let teamLayerVisible = true;           // viewer-local show/hide (not persisted)
let teamControlsEl: HTMLElement | null = null;

// ── Draw controller state ────────────────────────────────────────────────────
type TeamDrawMode = 'idle' | 'dropMarker' | 'drawCircle' | 'drawPolygon';
let teamDrawMode: TeamDrawMode = 'idle';
let teamDrawClick: ((e: any) => void) | null = null;
let teamDrawPreview: any = null;
let teamDrawHintEl: HTMLElement | null = null;
let teamPolyVerts: [number, number][] = [];   // polygon-in-progress vertices

// ── Breach toast state ───────────────────────────────────────────────────────
const BREACH_TOAST_TTL_MS = 6000;
const BREACH_STACK_MAX = 6;
let breachStackEl: HTMLElement | null = null;
let breachFlashLayer: any = null;

// ── Curated kind vocabularies ────────────────────────────────────────────────
// (the server takes any control-char-free <=32-char string; these are sensible
// defaults for the create UI).
const MARKER_KINDS = ['waypoint', 'hazard', 'rally', 'objective', 'supply', 'note'];
const GEOFENCE_KINDS = ['area', 'boundary', 'zone', 'objective', 'hazard', 'rally'];

// ── Style constants ──────────────────────────────────────────────────────────
const TEAM_FENCE_STYLE = {
  color: '#B84A22',            // --accent
  weight: 2, opacity: 0.9,
  dashArray: '6 5',            // dashed, to read as a boundary not a route
  fillColor: '#B84A22', fillOpacity: 0.07,
  className: 'team-fence',
};
const GEOFENCE_DRAW_STYLE = { color: '#8E371A', weight: 2, dashArray: '6 5', fillColor: '#B84A22', fillOpacity: 0.08 };

// ── Gate helpers ─────────────────────────────────────────────────────────────
// Gated-in = signed in + active device in a group + a 200 from /console has
// landed (team.seeded). The 200 IS the participant check (server-authoritative).
export function teamGatedIn(): boolean {
  return getAuthUserId() != null
    && !!MOCK.device
    && !!groupPathFor(MOCK.device.group)
    && team.seeded;
}
// Best-effort owner hint for the "+ Geofence" affordance: the caller owns the
// active device's token. The server is the real authority — a non-owner's write
// 403/404s and the UI toasts (see Phase 5). Never a security boundary.
// True iff the caller is the group's CLAIM OWNER (server-authoritative via the
// console `role`), not merely the active device's claimant. Gates owner-only
// affordances: the +Geofence tool, fence rename/delete, and owner moderation of
// any marker (markerEditable = author-or-owner). Falls to false until the first
// console fetch seeds team.role — so owner tools appear only once confirmed.
export function canManageGeofences(): boolean {
  return teamGatedIn() && team.role === 'owner';
}

// ── Marker authorship hint (ADR 0027) ────────────────────────────────────────
// created_by_user is never surfaced (ADR 0026), so the client can't know
// authorship from the read. Track own-created marker ids in localStorage per
// group as a best-effort Edit/Delete affordance — a HINT, not a boundary (the
// server enforces author-or-owner; a stale id 404s and is dropped).
// Pure localStorage helpers (mineMarkersKey/loadMineMarkers/saveMineMarkers/
// addMineMarker/removeMineMarker) live in storage/persistence.ts.
function isMineMarker(id: number): boolean {
  const path = groupPathFor(MOCK.device?.group ?? null);
  return path ? loadMineMarkers(path).has(id) : false;
}
// Edit/Delete affordance: own-this-client marker OR the (best-effort) group owner
// — who may moderate any marker (server enforces author-or-owner regardless).
export function markerEditable(m: Marker): boolean { return isMineMarker(m.id) || canManageGeofences(); }

// ── Popup HTML helpers ───────────────────────────────────────────────────────
function teamMarkerPopupHtml(m: Marker): string {
  const label = m.label ? escapeHtml(m.label) : '<span class="team-pop-empty">(no label)</span>';
  const actions = markerEditable(m)
    ? `<div class="team-pop-actions">` +
      `<button class="team-pop-btn" data-act="edit-marker"><i class="ph ph-pencil-simple"></i> Edit</button>` +
      `<button class="team-pop-btn danger" data-act="del-marker"><i class="ph ph-trash"></i> Delete</button></div>`
    : '';
  return `<div class="team-pop" data-marker="${m.id}"><div class="team-pop-label">${label}</div>`
    + `<div class="team-pop-kind">${escapeHtml(m.kind)}</div>${actions}</div>`;
}
function teamFencePopupHtml(f: Geofence): string {
  const label = f.label ? escapeHtml(f.label) : '<span class="team-pop-empty">(unnamed fence)</span>';
  const shape = 'circle' in f.shape
    ? `circle · ${Math.round(f.shape.circle.radius_m)} m`
    : `polygon · ${f.shape.polygon.length} pts`;
  const actions = canManageGeofences()
    ? `<div class="team-pop-actions">` +
      `<button class="team-pop-btn" data-act="edit-fence"><i class="ph ph-pencil-simple"></i> Rename</button>` +
      `<button class="team-pop-btn danger" data-act="del-fence"><i class="ph ph-trash"></i> Delete</button></div>`
    : '';
  return `<div class="team-pop" data-fence="${f.id}"><div class="team-pop-label">${label}</div>`
    + `<div class="team-pop-kind">${escapeHtml(f.kind)} · ${shape}</div>${actions}</div>`;
}

function addMarkerPin(layer: any, m: Marker){
  const icon = L.divIcon({
    className: 'team-marker-pin',
    html: '<i class="ph ph-map-pin" aria-hidden="true"></i>',
    iconSize: [26, 26], iconAnchor: [13, 24], popupAnchor: [0, -22],
  });
  const mk = L.marker([m.lat, m.lng], { icon })
    .bindTooltip(m.label ? escapeHtml(m.label) : '(no label)', { direction: 'top', offset: [0, -20] })
    .bindPopup(teamMarkerPopupHtml(m))
    .addTo(layer);
  mk.on('popupopen', (e: any) => {
    const root = e.popup.getElement();
    root?.querySelector('[data-act="edit-marker"]')?.addEventListener('click', () => { mk.closePopup(); openMarkerEditModal(m); });
    root?.querySelector('[data-act="del-marker"]')?.addEventListener('click', () => { mk.closePopup(); deleteMarker(m); });
  });
}
function addGeofenceShape(layer: any, f: Geofence){
  const shape = 'circle' in f.shape
    ? L.circle([f.shape.circle.lat, f.shape.circle.lng], { radius: f.shape.circle.radius_m, ...TEAM_FENCE_STYLE })
    : L.polygon((f.shape.polygon as [number, number][]).map(([lat, lng]) => [lat, lng]), TEAM_FENCE_STYLE);
  const label = f.label ? escapeHtml(f.label) : '(unnamed fence)';
  shape
    .bindTooltip(`${label} · ${escapeHtml(f.kind)}`, { direction: 'top', sticky: true })
    .bindPopup(teamFencePopupHtml(f))
    .addTo(layer);
  shape.on('popupopen', (e: any) => {
    const root = e.popup.getElement();
    root?.querySelector('[data-act="edit-fence"]')?.addEventListener('click', () => { shape.closePopup(); openFenceEditModal(f); });
    root?.querySelector('[data-act="del-fence"]')?.addEventListener('click', () => { shape.closePopup(); deleteGeofence(f); });
  });
}

// ── Layer helpers ────────────────────────────────────────────────────────────
function ensureTeamLayer(){
  if(!getLeafletMap()) return null;
  if(!teamLayer) teamLayer = L.layerGroup().addTo(getLeafletMap());
  return teamLayer;
}

// ── Team modal (reuses the gc-modal shell) ───────────────────────────────────
// docked = a bottom-pinned panel with a click-through transparent backdrop, so
// a live geofence preview on the map behind it stays fully visible (the radius
// preview UX). onClose fires on ✕/backdrop dismissal (callers clean up a preview).
export function openTeamModal(title: string, bodyHtml: string, opts: { onClose?: () => void; docked?: boolean } = {}): HTMLElement {
  document.getElementById('team-modal')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'team-modal';
  overlay.className = 'gc-modal-overlay' + (opts.docked ? ' team-modal-docked' : '');
  overlay.innerHTML =
    `<div class="gc-modal"><div class="gc-modal-head"><span>${escapeHtml(title)}</span>` +
    `<button class="gc-modal-close" id="team-modal-close" aria-label="Close">✕</button></div>` +
    `<div class="gc-modal-body">${bodyHtml}</div></div>`;
  const close = () => { overlay.remove(); opts.onClose?.(); };
  // Docked backdrop is pointer-events:none, so a click-out never reaches it.
  if(!opts.docked) overlay.addEventListener('click', (e) => { if(e.target === overlay) close(); });
  overlay.querySelector('#team-modal-close')?.addEventListener('click', close);
  document.body.appendChild(overlay);
  return overlay;
}

function kindOptions(kinds: string[], selected?: string): string {
  const list = selected && !kinds.includes(selected) ? [selected, ...kinds] : kinds;
  return list.map(k => `<option value="${escapeHtml(k)}"${k === selected ? ' selected' : ''}>${escapeHtml(k)}</option>`).join('');
}

// ── Marker CRUD ──────────────────────────────────────────────────────────────
function openMarkerCreateModal(lat: number, lng: number){
  const overlay = openTeamModal('New marker',
    `<label class="team-field"><span>Label (optional)</span>` +
    `<input id="marker-label" class="vis-input" type="text" maxlength="${MARKER_LABEL_LEN}" placeholder="e.g. water cache"></label>` +
    `<label class="team-field"><span>Kind</span><select id="marker-kind" class="vis-select">${kindOptions(MARKER_KINDS, 'waypoint')}</select></label>` +
    `<div class="team-modal-actions"><button class="btn btn--ghost" id="marker-cancel">Cancel</button>` +
    `<button class="btn btn--primary" id="marker-save">Place marker</button></div>`);
  overlay.querySelector('#marker-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.querySelector('#marker-save')?.addEventListener('click', () => {
    const label = ($('marker-label') as HTMLInputElement)?.value.trim() || null;
    const kind = ($('marker-kind') as HTMLSelectElement)?.value || 'waypoint';
    overlay.remove();
    void createMarker(lat, lng, label, kind);
  });
  setTimeout(() => ($('marker-label') as HTMLInputElement)?.focus(), 30);
}

function openMarkerEditModal(m: Marker){
  const overlay = openTeamModal('Edit marker',
    `<label class="team-field"><span>Label (optional)</span>` +
    `<input id="marker-label" class="vis-input" type="text" maxlength="${MARKER_LABEL_LEN}" value="${escapeHtml(m.label ?? '')}" placeholder="e.g. water cache"></label>` +
    `<label class="team-field"><span>Kind</span><select id="marker-kind" class="vis-select">${kindOptions(MARKER_KINDS, m.kind)}</select></label>` +
    `<div class="team-modal-actions"><button class="btn btn--ghost" id="marker-cancel">Cancel</button>` +
    `<button class="btn btn--primary" id="marker-save">Save</button></div>`);
  overlay.querySelector('#marker-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.querySelector('#marker-save')?.addEventListener('click', () => {
    const label = ($('marker-label') as HTMLInputElement)?.value.trim() || null;
    const kind = ($('marker-kind') as HTMLSelectElement)?.value || m.kind;
    overlay.remove();
    void updateMarker(m, label, kind);
  });
  setTimeout(() => ($('marker-label') as HTMLInputElement)?.focus(), 30);
}

async function createMarker(lat: number, lng: number, label: string | null, kind: string){
  const path = groupPathFor(MOCK.device?.group ?? null);
  if(!path) return;
  try {
    const res = await apiCreateMarker(path, lat, lng, label, kind);
    team.markers.push({ id: res.id, lat, lng, label, kind, created_at: res.created_at, updated_at: res.created_at });
    addMineMarker(path, res.id);
    renderTeamLayer();
    showToast('marker placed');
  } catch (err: any) {
    if(err?.status === 403) showToast('marker cap reached (200)');
    else if(err?.status === 404) showToast('claim this group first');
    else if(err?.status === 429) showToast('slow down — rate limited');
    else showToast('could not place marker');
  }
}
async function updateMarker(m: Marker, label: string | null, kind: string){
  const path = groupPathFor(MOCK.device?.group ?? null);
  if(!path) return;
  try {
    const res = await apiUpdateMarker(path, m.id, m.lat, m.lng, label, kind);
    m.label = label; m.kind = kind; m.updated_at = res.updated_at;
    renderTeamLayer();
    showToast('marker updated');
  } catch (err: any) {
    if(err?.status === 404){ showToast('you can only edit markers you created'); removeMineMarker(path, m.id); }
    else showToast('could not update marker');
  }
}
async function deleteMarker(m: Marker){
  if(!confirm('Delete this marker?')) return;
  const path = groupPathFor(MOCK.device?.group ?? null);
  if(!path) return;
  try {
    await apiDeleteMarker(path, m.id);
    team.markers = team.markers.filter(x => x.id !== m.id);
    removeMineMarker(path, m.id);
    renderTeamLayer();
    showToast('marker deleted');
  } catch (err: any) {
    if(err?.status === 404){ showToast('you can only delete markers you created'); removeMineMarker(path, m.id); }
    else showToast('could not delete marker');
  }
}

// ── Geofence CRUD ────────────────────────────────────────────────────────────
export function openFenceEditModal(f: Geofence){
  const overlay = openTeamModal('Edit geofence',
    `<div class="acct-hint">To reshape a fence, delete it and draw a new one.</div>` +
    `<label class="team-field"><span>Label (optional)</span>` +
    `<input id="gf-label" class="vis-input" type="text" maxlength="${MARKER_LABEL_LEN}" value="${escapeHtml(f.label ?? '')}"></label>` +
    `<label class="team-field"><span>Kind</span><select id="gf-kind" class="vis-select">${kindOptions(GEOFENCE_KINDS, f.kind)}</select></label>` +
    `<div class="team-modal-actions"><button class="btn btn--ghost" id="gf-cancel">Cancel</button>` +
    `<button class="btn btn--primary" id="gf-save">Save</button></div>`);
  overlay.querySelector('#gf-cancel')?.addEventListener('click', () => overlay.remove());
  overlay.querySelector('#gf-save')?.addEventListener('click', () => {
    const label = (overlay.querySelector('#gf-label') as HTMLInputElement)?.value.trim() || null;
    const kind = ($('gf-kind') as HTMLSelectElement)?.value || f.kind;
    overlay.remove();
    void updateGeofence(f, label, kind);
  });
  setTimeout(() => (overlay.querySelector('#gf-label') as HTMLInputElement)?.focus(), 30);
}

async function createGeofence(shape: GeofenceShape, label: string | null, kind: string){
  const path = groupPathFor(MOCK.device?.group ?? null);
  if(!path) return;
  try {
    const res = await apiCreateGeofence(path, shape, label, kind);
    const now = team.lastNow || Date.now();   // optimistic timestamps; the next poll corrects
    team.geofences.push({ id: res.id, shape, label, kind, created_at: now, updated_at: now });
    renderTeamLayer();
    showToast('geofence created');
  } catch (err: any) {
    if(err?.status === 403) showToast('geofence cap reached (32)');
    else if(err?.status === 404) showToast('only the group owner can add geofences');
    else if(err?.status === 429) showToast('slow down — rate limited');
    else showToast('could not create geofence');
  }
}
async function updateGeofence(f: Geofence, label: string | null, kind: string){
  const path = groupPathFor(MOCK.device?.group ?? null);
  if(!path) return;
  try {
    const res = await apiUpdateGeofence(path, f.id, f.shape, label, kind);   // shape unchanged (reshape = redraw)
    f.label = label; f.kind = kind; f.updated_at = res.updated_at;
    renderTeamLayer();
    showToast('geofence updated');
  } catch (err: any) {
    if(err?.status === 404) showToast('only the group owner can edit geofences');
    else showToast('could not update geofence');
  }
}
async function deleteGeofence(f: Geofence){
  if(!confirm('Delete this geofence?')) return;
  const path = groupPathFor(MOCK.device?.group ?? null);
  if(!path) return;
  try {
    await apiDeleteGeofence(path, f.id);
    team.geofences = team.geofences.filter(x => x.id !== f.id);
    renderTeamLayer();
    showToast('geofence deleted');
  } catch (err: any) {
    if(err?.status === 404) showToast('only the group owner can delete geofences');
    else showToast('could not delete geofence');
  }
}

// ── Draw controller ──────────────────────────────────────────────────────────
export function getTeamDrawMode(): TeamDrawMode { return teamDrawMode; }

function ensureDrawHint(): HTMLElement {
  if(teamDrawHintEl) return teamDrawHintEl;
  const el = document.createElement('div');
  el.className = 'team-draw-hint';
  el.id = 'team-draw-hint';
  (el as any).hidden = true;
  (document.querySelector('.map-body') || document.body).appendChild(el);
  teamDrawHintEl = el;
  return el;
}
function setDrawHint(html: string | null){
  const el = ensureDrawHint();
  if(html == null){ (el as any).hidden = true; el.innerHTML = ''; return; }
  el.innerHTML = html;
  (el as any).hidden = false;
}
// Tear down any active draw mode and return to idle (always clears the preview).
export function exitDrawMode(){
  if(teamDrawClick && getLeafletMap()) getLeafletMap().off('click', teamDrawClick);
  teamDrawClick = null;
  if(teamDrawPreview && getLeafletMap()){ getLeafletMap().removeLayer(teamDrawPreview); }
  teamDrawPreview = null;
  teamPolyVerts = [];
  teamDrawMode = 'idle';
  if(getLeafletMap()) getLeafletMap().getContainer().style.cursor = '';
  setDrawHint(null);
  renderTeamControls();
}

export function beginMarkerDraw(){
  if(!getLeafletMap() || !teamGatedIn()) return;
  if(teamDrawMode === 'dropMarker'){ exitDrawMode(); return; }   // toggle off
  if(team.markers.length >= MAX_MARKERS_PER_GROUP){ showToast('marker cap reached (200)'); return; }
  exitDrawMode();
  teamDrawMode = 'dropMarker';
  getLeafletMap().getContainer().style.cursor = 'crosshair';
  setDrawHint(`<i class="ph ph-map-pin" aria-hidden="true"></i> Click the map to place a marker` +
    `<button class="team-draw-cancel" id="team-draw-cancel">Cancel</button>`);
  $('team-draw-cancel')?.addEventListener('click', exitDrawMode);
  teamDrawClick = (e: any) => {
    const { lat, lng } = e.latlng;
    exitDrawMode();
    openMarkerCreateModal(lat, lng);
  };
  getLeafletMap().on('click', teamDrawClick);
  renderTeamControls();
}

/* ── Geofence draw / edit / delete (owner-only, Phase 5) ─────────────────────
   Custom Leaflet draw (no plugin): circle = click centre → radius input drives a
   live preview; polygon = click vertices (3..64) → Finish. Edit = rename/re-kind;
   to reshape, delete & redraw (interactive drag-edit deferred — ADR 0027). */
export function beginGeofenceDraw(){
  if(!getLeafletMap() || !canManageGeofences()) return;
  if(teamDrawMode === 'drawCircle' || teamDrawMode === 'drawPolygon'){ exitDrawMode(); return; }  // toggle off
  if(team.geofences.length >= MAX_GEOFENCES_PER_GROUP){ showToast('geofence cap reached (32)'); return; }
  const overlay = openTeamModal('New geofence',
    `<div class="acct-hint">Pick a shape, then draw it on the map.</div>` +
    `<div class="team-modal-actions">` +
    `<button class="btn btn--ghost" id="gf-circle"><i class="ph ph-circle"></i> Circle</button>` +
    `<button class="btn btn--ghost" id="gf-polygon"><i class="ph ph-polygon"></i> Polygon</button></div>`);
  overlay.querySelector('#gf-circle')?.addEventListener('click', () => { overlay.remove(); startCircleDraw(); });
  overlay.querySelector('#gf-polygon')?.addEventListener('click', () => { overlay.remove(); startPolygonDraw(); });
}

function startCircleDraw(){
  if(!getLeafletMap()) return;
  exitDrawMode();
  teamDrawMode = 'drawCircle';
  getLeafletMap().getContainer().style.cursor = 'crosshair';
  setDrawHint(`<i class="ph ph-circle" aria-hidden="true"></i> Click to set the circle centre` +
    `<button class="team-draw-cancel" id="team-draw-cancel">Cancel</button>`);
  $('team-draw-cancel')?.addEventListener('click', exitDrawMode);
  teamDrawClick = (e: any) => {
    if(teamDrawClick && getLeafletMap()){ getLeafletMap().off('click', teamDrawClick); teamDrawClick = null; }  // stop after centre
    openCircleModal(e.latlng.lat, e.latlng.lng);
  };
  getLeafletMap().on('click', teamDrawClick);
  renderTeamControls();
}
function openCircleModal(lat: number, lng: number){
  const defaultR = 100;
  if(teamDrawPreview && getLeafletMap()) getLeafletMap().removeLayer(teamDrawPreview);
  teamDrawPreview = L.circle([lat, lng], { radius: defaultR, ...GEOFENCE_DRAW_STYLE }).addTo(getLeafletMap());
  setDrawHint(null);
  const overlay = openTeamModal('New circle geofence',
    `<label class="team-field"><span>Radius (metres)</span>` +
    `<input id="gf-radius" class="vis-input" type="number" min="1" max="${MAX_GEOFENCE_RADIUS_M}" step="10" value="${defaultR}"></label>` +
    `<label class="team-field"><span>Label (optional)</span><input id="gf-label" class="vis-input" type="text" maxlength="${MARKER_LABEL_LEN}" placeholder="e.g. base camp"></label>` +
    `<label class="team-field"><span>Kind</span><select id="gf-kind" class="vis-select">${kindOptions(GEOFENCE_KINDS, 'area')}</select></label>` +
    `<div class="team-modal-actions"><button class="btn btn--ghost" id="gf-cancel">Cancel</button>` +
    `<button class="btn btn--primary" id="gf-save">Create</button></div>`,
    { onClose: exitDrawMode, docked: true });
  const radiusInput = overlay.querySelector('#gf-radius') as HTMLInputElement;
  const clampR = () => Math.max(1, Math.min(MAX_GEOFENCE_RADIUS_M, Number(radiusInput.value) || defaultR));
  radiusInput?.addEventListener('input', () => { teamDrawPreview?.setRadius(clampR()); });
  overlay.querySelector('#gf-cancel')?.addEventListener('click', () => { overlay.remove(); exitDrawMode(); });
  overlay.querySelector('#gf-save')?.addEventListener('click', () => {
    const r = clampR();
    const label = (overlay.querySelector('#gf-label') as HTMLInputElement)?.value.trim() || null;
    const kind = ($('gf-kind') as HTMLSelectElement)?.value || 'area';
    overlay.remove();
    exitDrawMode();
    void createGeofence({ circle: { lat, lng, radius_m: r } }, label, kind);
  });
}

function startPolygonDraw(){
  if(!getLeafletMap()) return;
  exitDrawMode();
  teamDrawMode = 'drawPolygon';
  teamPolyVerts = [];
  getLeafletMap().getContainer().style.cursor = 'crosshair';
  updatePolygonHint();
  teamDrawClick = (e: any) => {
    if(teamPolyVerts.length >= MAX_POLYGON_VERTICES){ showToast(`max ${MAX_POLYGON_VERTICES} vertices — finish or cancel`); return; }
    teamPolyVerts.push([e.latlng.lat, e.latlng.lng]);
    redrawPolygonPreview();
    updatePolygonHint();
  };
  getLeafletMap().on('click', teamDrawClick);
  renderTeamControls();
}
function updatePolygonHint(){
  const n = teamPolyVerts.length;
  const canFinish = n >= MIN_POLYGON_VERTICES;
  setDrawHint(`<i class="ph ph-polygon" aria-hidden="true"></i> ${n}/${MAX_POLYGON_VERTICES} points` +
    (canFinish ? `<button class="team-draw-finish" id="team-draw-finish">Finish</button>` : ` · need ${MIN_POLYGON_VERTICES}+`) +
    `<button class="team-draw-cancel" id="team-draw-cancel">Cancel</button>`);
  $('team-draw-finish')?.addEventListener('click', finishPolygon);
  $('team-draw-cancel')?.addEventListener('click', exitDrawMode);
}
function redrawPolygonPreview(){
  if(!getLeafletMap()) return;
  if(teamDrawPreview) getLeafletMap().removeLayer(teamDrawPreview);
  const grp = L.layerGroup();
  if(teamPolyVerts.length >= 3){
    L.polygon(teamPolyVerts, { ...GEOFENCE_DRAW_STYLE, weight: 1 }).addTo(grp);
  } else if(teamPolyVerts.length === 2){
    L.polyline(teamPolyVerts, { color: GEOFENCE_DRAW_STYLE.color, weight: 2, dashArray: '6 5' }).addTo(grp);
  }
  for(const v of teamPolyVerts){
    L.circleMarker(v, { radius: 4, color: GEOFENCE_DRAW_STYLE.color, weight: 2, fillColor: '#fff8ed', fillOpacity: 1 }).addTo(grp);
  }
  grp.addTo(getLeafletMap());
  teamDrawPreview = grp;
}
function finishPolygon(){
  if(teamPolyVerts.length < MIN_POLYGON_VERTICES){ showToast(`need at least ${MIN_POLYGON_VERTICES} points`); return; }
  const verts = teamPolyVerts.slice();
  if(teamDrawClick && getLeafletMap()){ getLeafletMap().off('click', teamDrawClick); teamDrawClick = null; }  // keep preview while naming
  setDrawHint(null);
  const overlay = openTeamModal('New polygon geofence',
    `<div class="acct-hint">${verts.length} vertices.</div>` +
    `<label class="team-field"><span>Label (optional)</span><input id="gf-label" class="vis-input" type="text" maxlength="${MARKER_LABEL_LEN}" placeholder="e.g. patrol zone"></label>` +
    `<label class="team-field"><span>Kind</span><select id="gf-kind" class="vis-select">${kindOptions(GEOFENCE_KINDS, 'area')}</select></label>` +
    `<div class="team-modal-actions"><button class="btn btn--ghost" id="gf-cancel">Cancel</button>` +
    `<button class="btn btn--primary" id="gf-save">Create</button></div>`,
    { onClose: exitDrawMode, docked: true });
  overlay.querySelector('#gf-cancel')?.addEventListener('click', () => { overlay.remove(); exitDrawMode(); });
  overlay.querySelector('#gf-save')?.addEventListener('click', () => {
    const label = (overlay.querySelector('#gf-label') as HTMLInputElement)?.value.trim() || null;
    const kind = ($('gf-kind') as HTMLSelectElement)?.value || 'area';
    overlay.remove();
    exitDrawMode();
    void createGeofence({ polygon: verts }, label, kind);
  });
}

// ── Team controls render ─────────────────────────────────────────────────────
function ensureTeamControls(): HTMLElement | null {
  if(teamControlsEl) return teamControlsEl;
  const body = document.querySelector('.map-body');   // first = the tracker map (not world)
  if(!body) return null;
  const el = document.createElement('div');
  el.className = 'team-controls';
  el.id = 'team-controls';
  (el as any).hidden = true;
  body.appendChild(el);
  teamControlsEl = el;
  return el;
}
export function renderTeamControls(){
  const el = ensureTeamControls();
  if(!el) return;
  if(!teamGatedIn()){ (el as any).hidden = true; el.innerHTML = ''; return; }
  (el as any).hidden = false;
  const toggle =
    `<button class="team-controls__btn${teamLayerVisible ? ' is-on' : ''}" id="team-toggle" ` +
    `title="${teamLayerVisible ? 'Hide team layer' : 'Show team layer'}" aria-pressed="${teamLayerVisible}">` +
    `<i class="ph ${teamLayerVisible ? 'ph-eye' : 'ph-eye-slash'}" aria-hidden="true"></i></button>`;
  const markerCap = team.markers.length >= MAX_MARKERS_PER_GROUP;
  const addMarker =
    `<button class="team-controls__btn${teamDrawMode === 'dropMarker' ? ' is-active' : ''}" id="team-add-marker" ` +
    `${markerCap ? 'disabled ' : ''}title="${markerCap ? 'Marker cap reached (200)' : 'Add a marker'}">` +
    `<i class="ph ph-map-pin" aria-hidden="true"></i></button>`;
  const fenceCap = team.geofences.length >= MAX_GEOFENCES_PER_GROUP;
  const addFence = canManageGeofences()
    ? `<button class="team-controls__btn${(teamDrawMode === 'drawCircle' || teamDrawMode === 'drawPolygon') ? ' is-active' : ''}" id="team-add-fence" ` +
      `${fenceCap ? 'disabled ' : ''}title="${fenceCap ? 'Geofence cap reached (32)' : 'Add a geofence (owner only)'}">` +
      `<i class="ph ph-polygon" aria-hidden="true"></i></button>`
    : '';
  el.innerHTML =
    `<div class="team-controls__group">${toggle}</div>` +
    `<div class="team-controls__group">${addMarker}${addFence}</div>`;
  $('team-toggle')?.addEventListener('click', () => { teamLayerVisible = !teamLayerVisible; renderTeamLayer(); });
  $('team-add-marker')?.addEventListener('click', beginMarkerDraw);
  $('team-add-fence')?.addEventListener('click', beginGeofenceDraw);
}

// Clear + rebuild the team layer from `team` state. Called from pollConsole()
// (per 30 s poll) and clearTeam(). Attaches ONLY to leafletMap. The control
// cluster updates every call so role/gating changes are reflected.
export function renderTeamLayer(){
  renderTeamControls();
  if(!getLeafletMap()) return;
  const layer = ensureTeamLayer();
  if(!layer) return;
  layer.clearLayers();
  if(!teamLayerVisible || !teamGatedIn()) return;
  for(const f of team.geofences) addGeofenceShape(layer, f);   // fences under pins
  for(const m of team.markers) addMarkerPin(layer, m);
}

// ── Team poll ────────────────────────────────────────────────────────────────
// Drop the team layer back to empty (signed out, left the group, or 404) and
// re-render so any drawn shapes/pins disappear. Resets the breach baseline so a
// later re-entry seeds fresh (no replay of history).
export function clearTeam(){
  team.markers = [];
  team.geofences = [];
  team.events = [];
  team.lastNow = 0;
  team.lastSeenObservedAt = null;
  team.seeded = false;
  team.role = null;
  renderTeamLayer();
}

export async function pollConsole(){
  if(document.hidden) return;                 // match pollGroup: skip hidden-tab ticks
  // Internal gate — the loop may be running for an anonymous/ungrouped session
  // (cheap no-op, no network) and engages the moment the user is signed in + grouped.
  if(getAuthUserId() == null){ clearTeam(); return; }
  if(!MOCK.device){ clearTeam(); return; }
  const path = groupPathFor(MOCK.device.group);
  if(!path){ clearTeam(); return; }
  let data;
  try {
    const since = consolePollLastNow != null
      ? Math.max(0, consolePollLastNow - GROUP_POLL_OVERLAP_MS)
      : null;
    data = await apiGetGroupConsole(path, since);
  } catch (err: any) {
    if(err?.status === 404){
      // Not a participant / claim lapsed. Clear and stop quietly. The loop
      // restarts on re-entering the map (show('map')) OR on an in-drawer group
      // claim (claimCurrentGroup() calls startConsolePoll() on success) — the
      // latter is why a drawer claim no longer needs a map round-trip to seed.
      clearTeam();
      stopConsolePoll();
      return;
    }
    console.warn('[locus] console poll failed', err);
    return;                                   // transient — keep the loop + last layer
  }
  // ADDITIVE: read ONLY markers / geofences / events. state.cohort + the trail
  // render are untouched (teammate trails stay the coarsened `recent` ones).
  team.markers = data.markers;
  team.geofences = data.geofences;
  team.events = data.geofence_events;
  team.lastNow = data.now;
  team.role = data.role;
  consolePollLastNow = data.now;

  // Breach seen-baseline (no-spam-on-load, mirrors the device's first-seen-never-
  // fires ethos): the FIRST successful fetch seeds the baseline WITHOUT toasting;
  // later fetches surface only events newer than it, oldest-first.
  const events = data.geofence_events;
  const maxObserved = events.reduce((m, e) => Math.max(m, e.observed_at), 0);
  if(!team.seeded){
    team.lastSeenObservedAt = maxObserved || data.now;
    team.seeded = true;
  } else {
    const baseline = team.lastSeenObservedAt ?? 0;
    const fresh = events.filter(e => e.observed_at > baseline).sort((a, b) => a.observed_at - b.observed_at);
    for(const ev of fresh) showBreachToast(ev);
    if(maxObserved > baseline) team.lastSeenObservedAt = maxObserved;
  }
  renderTeamLayer();
}

export function startConsolePoll(){
  if(consolePollTimer) return;                // idempotent
  pollConsole();                              // fire immediately (gates internally)
  consolePollTimer = setInterval(pollConsole, 30_000);
}

export function stopConsolePoll(){
  if(consolePollTimer){ clearInterval(consolePollTimer); consolePollTimer = null; }
  consolePollLastNow = null;
}

// ── Breach toasts (ADR 0027, Phase 2) ────────────────────────────────────────
// A top-right, stackable, CLICKABLE toast distinct from the single-pill
// showToast: a geofence enter/exit a teammate's device reported. Driven from
// pollConsole() for events newer than the seen-baseline. Click pans + flashes
// the fence on the tracker map. No person attribution — device tag only.
function ensureBreachStack(): HTMLElement {
  if(breachStackEl) return breachStackEl;
  const el = document.createElement('div');
  el.className = 'breach-stack';
  el.id = 'breach-stack';
  document.body.appendChild(el);
  breachStackEl = el;
  return el;
}

function geofenceById(id: number): Geofence | undefined {
  return team.geofences.find(f => f.id === id);
}
// Pan target for a fence: a circle's centre, or a polygon's vertex mean.
function fenceCenter(f: Geofence): [number, number] | null {
  if('circle' in f.shape) return [f.shape.circle.lat, f.shape.circle.lng];
  const pts = f.shape.polygon as [number, number][];
  if(!pts.length) return null;
  const lat = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const lng = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  return [lat, lng];
}
// Pan the tracker map to a fence and flash a bold copy of its outline (a
// transient layer on getLeafletMap(), removed after ~1.6 s — independent of the
// team layer so a poll re-render can't strand it).
export function panToFence(f: Geofence){
  if(!getLeafletMap()) return;
  const center = fenceCenter(f);
  if(!center) return;
  getLeafletMap().flyTo(center, Math.max(getLeafletMap().getZoom(), 14), { duration: 0.5 });
  if(breachFlashLayer){ getLeafletMap().removeLayer(breachFlashLayer); breachFlashLayer = null; }
  const style = { color: '#8E371A', weight: 4, opacity: 1, fill: false, className: 'breach-flash' };
  const fl = 'circle' in f.shape
    ? L.circle([f.shape.circle.lat, f.shape.circle.lng], { radius: f.shape.circle.radius_m, ...style })
    : L.polygon((f.shape.polygon as [number, number][]).map(([lat, lng]) => [lat, lng]), style);
  fl.addTo(getLeafletMap());
  breachFlashLayer = fl;
  setTimeout(() => {
    if(getLeafletMap() && fl) getLeafletMap().removeLayer(fl);
    if(breachFlashLayer === fl) breachFlashLayer = null;
  }, 1600);
}

export function showBreachToast(ev: GeofenceEvent){
  const stack = ensureBreachStack();
  const fence = geofenceById(ev.geofence_id);
  const fenceLabel = fence?.label || 'a geofence';
  const tag = ev.tag || 'a device';
  const verb = ev.transition === 'enter' ? 'entered' : 'exited';
  const el = document.createElement('button');
  el.type = 'button';
  el.className = `breach-toast breach-toast--${ev.transition}`;
  el.innerHTML =
    `<span class="breach-dot" aria-hidden="true"></span>` +
    `<span class="breach-text"><b>${escapeHtml(tag)}</b> ${verb} ` +
    `<span class="breach-fence">${escapeHtml(fenceLabel)}</span></span>`;
  let timer: any = null;
  const dismiss = () => {
    if(timer){ clearTimeout(timer); timer = null; }
    el.classList.remove('is-in');
    el.classList.add('is-out');
    setTimeout(() => el.remove(), 220);
  };
  el.addEventListener('click', () => { if(fence) panToFence(fence); dismiss(); });
  // Hover pauses the auto-dismiss so a toast can be read/clicked.
  el.addEventListener('mouseenter', () => { if(timer){ clearTimeout(timer); timer = null; } });
  el.addEventListener('mouseleave', () => { if(!timer) timer = setTimeout(dismiss, 1500); });
  stack.appendChild(el);
  requestAnimationFrame(() => el.classList.add('is-in'));   // slide-in
  timer = setTimeout(dismiss, BREACH_TOAST_TTL_MS);
  while(stack.children.length > BREACH_STACK_MAX) stack.firstElementChild?.remove();
}
